import React, { useEffect, useMemo, useCallback } from 'react';
import { useStore } from 'zustand/react';
import { useShallow } from 'zustand/react/shallow';
import { createPositionAutomationStore } from '@stores/positionAutomation/PositionAutomation.store';
import {
    selectEditor,
    selectAutomationRules,
    selectEditorLoaded,
    selectRulesByStep,
    selectRulesByOutcome,
} from '@stores/positionAutomation/PositionAutomation.selectors';
import AutomationRuleModal from './components/AutomationRuleModal';
import AutomationSection from './components/AutomationSection';

export function useRecruitmentFlowAutomations(
    jobId,
    { planSteps = [], savedStepIds = new Set(), isRecruitmentFlowChanged = false } = {},
) {
    const store = useMemo(() => createPositionAutomationStore(jobId), [jobId]);

    const editor = useStore(store, useShallow(selectEditor));
    const automationRules = useStore(store, selectAutomationRules);
    const editorLoaded = useStore(store, selectEditorLoaded);
    const rulesByStep = useStore(store, selectRulesByStep);
    const rulesByOutcome = useStore(store, selectRulesByOutcome);

    const fetchConfigs = useStore(store, (s) => s.fetchConfigs);
    const fetchEditor = useStore(store, (s) => s.fetchEditor);

    useEffect(() => {
        if (!jobId) {
            return undefined;
        }
        const controller = new AbortController();
        fetchEditor(controller.signal);
        return () => controller.abort();
    }, [jobId, fetchEditor]);

    useEffect(() => {
        if (editorLoaded) {
            fetchConfigs({ notification: false });
        }
    }, [editorLoaded, fetchConfigs]);

    const renderAutomationRuleModal = useCallback(
        () => <AutomationRuleModal store={store} />,
        [store],
    );

    const renderAutomationsForContext = useCallback(
        (contextId, isOutcome = false) => (
            <AutomationSection
                key={`automation-${isOutcome ? 'outcome' : 'step'}-${contextId}`}
                store={store}
                contextId={contextId}
                isOutcome={isOutcome}
                savedStepIds={savedStepIds}
                isRecruitmentFlowChanged={isRecruitmentFlowChanged}
            />
        ),
        [store, savedStepIds, isRecruitmentFlowChanged],
    );

    return {
        automationRules,
        rulesByStep,
        rulesByOutcome,
        loading: editor.loading,
        error: editor.error,
        renderAutomationsForContext,
        renderAutomationRuleModal,
    };
}
